import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { reportService } from '../../services/reportService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import toast from 'react-hot-toast';
import {
  DocumentArrowDownIcon,
  ChartBarIcon,
  UserGroupIcon,
  CreditCardIcon,
  BanknotesIcon,
  CalendarIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';
import MemberReport from './MemberReport';
import LoanReport from './LoanReport';

const ReportGenerator = () => {
  const [reportType, setReportType] = useState('member-summary');
  const [dateRange, setDateRange] = useState({
    from_date: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    to_date: new Date().toISOString().split('T')[0]
  });
  const [filters, setFilters] = useState({
    branch_id: '',
    status: '',
    loan_type: ''
  });
  const [isExporting, setIsExporting] = useState(false);

  const buildParams = (base, filters) => {
    const params = { ...base };

    if (filters.branch_id) {
        params.branch_id = filters.branch_id;
    }

    if (filters.loan_type) {
        params.loan_type = filters.loan_type;
    }

    return params;
  };

  // Fetch member summary report
  const { data: memberSummary, isLoading: memberLoading, refetch: refetchMember } = useQuery(
    ['member-summary', filters.branch_id],
    () => {
        const params = {};

        if (filters.branch_id) {
        params.branch_id = filters.branch_id;
        }

        return reportService.getMemberSummary(params);
    },
    { enabled: reportType === 'member-summary' }
  );

  // Fetch loan performance report
  const { data: loanPerformance, isLoading: loanLoading, refetch: refetchLoan } = useQuery(
    ['loan-performance', dateRange, filters.branch_id, filters.loan_type],
    () =>
        reportService.getLoanPerformance(
        buildParams(
            {
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
            },
            filters
        )
        ),
    { enabled: reportType === 'loan-performance' }
  );

  // Fetch collection report
  const { data: collectionReport, isLoading: collectionLoading, refetch: refetchCollection } = useQuery(
    ['collection-report', dateRange, filters.branch_id],
    () =>
        reportService.getCollectionReport(
        buildParams(
            {
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
            },
            filters
        )
        ),
    { enabled: reportType === 'collection-report' }
  );

  // Fetch savings summary
  const { data: savingsSummary, isLoading: savingsLoading, refetch: refetchSavings } = useQuery(
    ['savings-summary', filters.branch_id],
    () => {
        const params = {};

        if (filters.branch_id) {
        params.branch_id = filters.branch_id;
        }

        return reportService.getSavingsSummary(params);
    },
    { enabled: reportType === 'savings-summary' }
  );

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      let blob;
      if (reportType === 'member-summary') {
        const params = buildParams({}, filters);
        blob = await reportService.exportMemberList(params);
      } else if (reportType === 'loan-performance') {
        const params = buildParams(
          {
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
          },
          filters
        );
        blob = await reportService.exportLoanReport(params);
      } else if (reportType === 'collection-report') {
        const params = buildParams(
          {
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
          },
          filters
        );
        blob = await reportService.exportCollectionReport(params);
      } else {
        toast.error('Export not available for this report type');
        setIsExporting(false);
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleRefresh = () => {
    if (reportType === 'member-summary') refetchMember();
    else if (reportType === 'loan-performance') refetchLoan();
    else if (reportType === 'collection-report') refetchCollection();
    else if (reportType === 'savings-summary') refetchSavings();
    toast.success('Report refreshed');
  };

  const reportTypes = [
    { id: 'member-summary', name: 'Member Summary', icon: UserGroupIcon, color: 'bg-blue-500' },
    { id: 'loan-performance', name: 'Loan Performance', icon: CreditCardIcon, color: 'bg-green-500' },
    { id: 'collection-report', name: 'Collection Report', icon: BanknotesIcon, color: 'bg-purple-500' },
    { id: 'savings-summary', name: 'Savings Summary', icon: ChartBarIcon, color: 'bg-orange-500' }
  ];

  const isLoading = memberLoading || loanLoading || collectionLoading || savingsLoading;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Reports & Analytics</h1>
        <div className="flex space-x-2">
          <button
            onClick={handleRefresh}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center"
          >
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center"
          >
            <PrinterIcon className="h-5 w-5 mr-2" />
            Print
          </button>
          <button
            onClick={() => handleExport('excel')}
            disabled={isExporting}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </button>
        </div>
      </div>

      {/* Report Type Selection */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {reportTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => setReportType(type.id)}
            className={`p-4 rounded-lg border-2 transition-all ${
              reportType === type.id
                ? `${type.color} text-white border-transparent`
                : 'bg-white border-gray-200 hover:border-gray-300'
            }`}
          >
            <type.icon className={`h-8 w-8 mx-auto mb-2 ${reportType === type.id ? 'text-white' : 'text-gray-400'}`} />
            <div className="font-medium">{type.name}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {(reportType === 'loan-performance' || reportType === 'collection-report') && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">From Date</label>
                <input
                  type="date"
                  value={dateRange.from_date}
                  onChange={(e) => setDateRange({ ...dateRange, from_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">To Date</label>
                <input
                  type="date"
                  value={dateRange.to_date}
                  onChange={(e) => setDateRange({ ...dateRange, to_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">Branch</label>
            <select
              value={filters.branch_id}
              onChange={(e) => setFilters({ ...filters, branch_id: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Branches</option>
              <option value="1">Head Office</option>
              <option value="2">Dhaka Branch</option>
              <option value="3">Chittagong Branch</option>
            </select>
          </div>

          {(reportType === 'member-summary' || reportType === 'loan-performance') && (
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          )}

          {reportType === 'loan-performance' && (
            <div>
              <label className="block text-sm font-medium mb-2">Loan Type</label>
              <select
                value={filters.loan_type}
                onChange={(e) => setFilters({ ...filters, loan_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">All Types</option>
                <option value="general">General</option>
                <option value="emergency">Emergency</option>
                <option value="agriculture">Agriculture</option>
                <option value="business">Business</option>
                <option value="education">Education</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Report Content */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="p-6">
            {/* Member Summary Report */}
            {reportType === 'member-summary' && memberSummary?.data && (
              <MemberReport data={memberSummary.data} />
            )}

            {/* Loan Performance Report */}
            {reportType === 'loan-performance' && loanPerformance?.data && (
              <LoanReport data={loanPerformance.data} period={dateRange} />
            )}

            {/* Collection Report */}
            {reportType === 'collection-report' && collectionReport?.data && (
              <div>
                <div className="mb-6">
                  <h2 className="text-xl font-bold">Collection Report</h2>
                  <p className="text-gray-600">
                    Period: {formatDate(dateRange.from_date)} - {formatDate(dateRange.to_date)}
                  </p>
                </div>

                {/* Collection Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
                    <div className="text-sm opacity-90">Total Collection</div>
                    <div className="text-2xl font-bold">
                      {formatCurrency(collectionReport.data.summary?.total_collection || 0)}
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
                    <div className="text-sm opacity-90">Total Savings</div>
                    <div className="text-2xl font-bold">
                      {formatCurrency(collectionReport.data.summary?.total_savings || 0)}
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
                    <div className="text-sm opacity-90">Loan Collection</div>
                    <div className="text-2xl font-bold">
                      {formatCurrency(collectionReport.data.summary?.total_loan_collection || 0)}
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-4 text-white">
                    <div className="text-sm opacity-90">Average Daily</div>
                    <div className="text-2xl font-bold">
                      {formatCurrency(collectionReport.data.summary?.average_daily || 0)}
                    </div>
                  </div>
                </div>

                {/* Collection Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Samity</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Officer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Savings</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loan</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Members</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(collectionReport.data.data || []).map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(item.collection_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.samity_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.officer_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.total_savings)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.total_loan_installments)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            {formatCurrency(item.total_amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.members_served}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="3" className="px-6 py-4 text-right font-bold">Total:</td>
                        <td className="px-6 py-4 font-bold">{formatCurrency(collectionReport.data.summary?.total_savings || 0)}</td>
                        <td className="px-6 py-4 font-bold">{formatCurrency(collectionReport.data.summary?.total_loan_collection || 0)}</td>
                        <td className="px-6 py-4 font-bold">{formatCurrency(collectionReport.data.summary?.total_collection || 0)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Savings Summary Report */}
            {reportType === 'savings-summary' && savingsSummary?.data && (
              <div>
                <div className="mb-6">
                  <h2 className="text-xl font-bold">Savings Summary Report</h2>
                  <p className="text-gray-600">As of {formatDate(new Date())}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg p-4 text-white">
                    <div className="text-sm opacity-90">Total Deposits</div>
                    <div className="text-2xl font-bold">
                      {formatCurrency(savingsSummary.data.total_deposits || 0)}
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-pink-500 to-pink-600 rounded-lg p-4 text-white">
                    <div className="text-sm opacity-90">Total Accounts</div>
                    <div className="text-2xl font-bold">
                      {savingsSummary.data.total_accounts || 0}
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg p-4 text-white">
                    <div className="text-sm opacity-90">Active Accounts</div>
                    <div className="text-2xl font-bold">
                      {savingsSummary.data.active_accounts || 0}
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scheme Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Accounts</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Deposits</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Average Balance</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interest Rate</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(savingsSummary.data.savings_by_type || []).map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 capitalize">
                            {item.scheme_type?.replace('_', ' ')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.total_accounts}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.total_deposits)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.total_deposits / item.total_accounts)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.avg_interest_rate}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportGenerator;